/**
 * voice-v8 — Persona-API-Client (Phase J + Profiles).
 *
 * Wrappt /api/persona für Bot-Name + Soul-Text + Profile-Picker.
 * User-spezifisch via Session-Cookie.
 */

export interface SoulProfileMeta {
  key: string;
  name: string;
  label: string;
  description: string;
  /** Pre.155: Avatar-Bild-URL (z.B. /avatars/baerli.png). */
  avatarUrl?: string;
  /** Pre.162: 128px webp Thumbnail (z.B. /avatars/baerli.thumb.webp). */
  avatarThumbUrl?: string;
}

export interface PersonaConfig {
  botName: string;
  soulText: string;
  profileKey: string;
  isDefault: boolean;
  availableProfiles: SoulProfileMeta[];
  /** Pre.155: Custom-Avatar wenn User selbst eins hochgeladen hat. */
  customAvatarUrl?: string | null;
  /** 2026-07-01 (Martin): Gewaehlter Avatar (Symbol-Profil-Key oder "custom"),
   *  entkoppelt von profileKey. Null = folgt profileKey. */
  avatarKey?: string | null;
  /** 2026-08-29: die innere Ausrichtung — sichtbar statt verborgen. */
  innerGuidance?: string | null;
  innerGuidanceAktiv?: boolean;
}

export async function uploadCustomAvatar(file: File): Promise<{
  ok: boolean;
  customAvatarUrl?: string;
  error?: string;
}> {
  const fd = new FormData();
  fd.append("avatar", file);
  const res = await fetch("/api/persona/avatar", {
    method: "POST",
    body: fd,
    credentials: "same-origin",
  });
  if (!res.ok) {
    let detail = `http-${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) detail = body.error;
    } catch {
      /* not JSON */
    }
    return { ok: false, error: detail };
  }
  return (await res.json()) as { ok: boolean; customAvatarUrl?: string };
}

export async function fetchPersona(): Promise<PersonaConfig> {
  const res = await fetch("/api/persona", { credentials: "same-origin" });
  if (!res.ok) {
    throw new Error(`fetch persona failed: ${res.status}`);
  }
  return (await res.json()) as PersonaConfig;
}

/**
 * Welches Bild die Persona gerade meint. Eine Stelle, zwei Leser:
 * Chat/Topbar und Zuhause. custom-Foto vor gewähltem Symbol vor Profil.
 */
export function avatarUrlAus(cfg: PersonaConfig): string | null {
  const avatarKey = cfg.avatarKey ?? "";
  if (avatarKey === "custom" && cfg.customAvatarUrl) return cfg.customAvatarUrl;
  if (avatarKey && avatarKey !== "custom") {
    const ap = cfg.availableProfiles.find((p) => p.key === avatarKey);
    return ap?.avatarThumbUrl ?? ap?.avatarUrl ?? null;
  }
  if (cfg.customAvatarUrl) return cfg.customAvatarUrl;
  const profile = cfg.availableProfiles.find((p) => p.key === cfg.profileKey);
  return profile?.avatarThumbUrl ?? profile?.avatarUrl ?? null;
}

export async function savePersona(input: {
  botName: string;
  soulText: string;
  profileKey?: string;
  avatarKey?: string;
  customAvatarUrl?: string | null;
  /**
   * 2026-08-29: Schalter fuer die innere Ausrichtung.
   *
   * ⚠️ Fehlt er, bewahrt der Server den bestehenden Wert. Ein gewoehnliches
   * Persona-Speichern darf die Ausrichtung weder ein- noch ausschalten.
   */
  innerGuidanceAktiv?: boolean;
}): Promise<PersonaConfig> {
  const res = await fetch("/api/persona", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(`save persona failed: ${res.status}`);
  }
  const data = (await res.json()) as { ok: boolean } & PersonaConfig;
  return data;
}

export async function resetPersona(): Promise<PersonaConfig> {
  return savePersona({ botName: "", soulText: "", profileKey: "" });
}

